import { useEffect, useState } from "react";
import { useMutation, useQuery, useReactiveVar } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import _, { cloneDeep } from "lodash";
import mixpanel from "mixpanel-browser";

import {
  EFilterColorMethod,
  EOrganizationType,
  EPropertyFilter,
  IObjectToIFiltersData,
  IObjectToIUnitDetailsDataHomeAmount,
  IRegion,
  IRegionIdVariable,
  IUnitDetailsDataHome,
} from "../../interfaces/interfaces";
import {
  organizationIdNameVar,
  smallHeaderBackArrow,
  smallHeaderDescription,
  smallHeaderTitle,
  userIdVar,
} from "../apollo/LocalState";
import { ReactComponent as AlertTriangle } from "../../img/AlertTriangle.svg";
import { ReactComponent as Bell } from "../../img/propertyDetails/Bell.svg";
import Breadcrumbs from "../breadcrumbs/Breadcrumbs";
import { ReactComponent as Building } from "../../img/propertyDetails/Building.svg";
import { ADD_HOME_FOR_REGION, GET_REGION } from "../../api/gqlQueries";
import { ReactComponent as Homes } from "../../img/propertyDetails/homes.svg";
import { ReactComponent as Search } from "../../img/propertyDetails/Search.svg";
import Spinner from "../Spinner";
import BaseRegistrationModal from "../modals/BaseRegistrationModal";
import HomeForm from "../organizations/HomeForm";
import { ReactComponent as CreateHomeIcon } from "../../img/propertyDetails/Home.svg";

import FilterList from "./FilterList";
import FilterListSelect from "./FilterListSelect";
import Floor from "./Floor";

interface CreateHomeModalProps {
  newHomeValue: string;
  setNewHomeValue: React.Dispatch<React.SetStateAction<string>>;
  closeModal: () => void;
  propertyId: string;
}

const CreateHomeModal: React.FC<CreateHomeModalProps> = ({
  closeModal,
  newHomeValue,
  setNewHomeValue,
  propertyId,
}) => {
  const userId = useReactiveVar(userIdVar);
  const organizationIdName = useReactiveVar(organizationIdNameVar);
  const [createHome] = useMutation(ADD_HOME_FOR_REGION);
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);
    const homeName = formData.get("home-name") as string;
    createHome({
      update: (cache, { data }) => {
        if (!data) {
          // No need to update cache if no data returned.
          return;
        }
        const cachedData = cache.readQuery<
          IRegionDetailsData,
          IRegionIdVariable
        >({
          query: GET_REGION,
          variables: { regionPropertyId: propertyId },
        });
        if (!cachedData) {
          return;
        }
        const updatedData = cloneDeep(cachedData);
        updatedData.region.homes.push(data.addHomeForRegion.home);
        cache.writeQuery({
          data: updatedData,
          query: GET_REGION,
          variables: { regionPropertyId: propertyId },
        });
        closeModal();
      },
      variables: { homeName, regionPropertyId: propertyId },
    });
    if (
      organizationIdName.organizationType ===
      EOrganizationType.PRO_VIEW_ORGANIZATION
    ) {
      mixpanel.track("Home Added", {
        new_home_name: homeName,
        organization_id: organizationIdName.organizationId,
        organization_name: organizationIdName.organizationName,
        organization_type: organizationIdName.organizationType,
        user_id: userId,
      });
    }
    setNewHomeValue("");
  };
  return (
    <BaseRegistrationModal
      closeModalFn={closeModal}
      headerText="Add New Home"
      Image={<CreateHomeIcon className="organization-create-img" />}
    >
      <div className="margin-bottom-small menu">Home Details</div>
      <HomeForm
        closeModal={closeModal}
        newHomeValue={newHomeValue}
        setNewHomeValue={setNewHomeValue}
        handleSubmit={handleSubmit}
      />
    </BaseRegistrationModal>
  );
};

export interface IRegionDetailsData {
  region: IRegion;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filteredMethods: any = {
  [EPropertyFilter.notFiltered]: {},
  [EPropertyFilter.withAlerts]: (sortedHome: IUnitDetailsDataHome) => {
    return sortedHome.alerts.length !== 0;
  },
  [EPropertyFilter.withWarnings]: (sortedHome: IUnitDetailsDataHome) => {
    return sortedHome.warnings.length !== 0;
  },
};

export const FilterItemsData: IObjectToIFiltersData = {
  [EPropertyFilter.notFiltered]: {
    cssColorMethod: EFilterColorMethod.FILL,
    filterTypeEnum: EPropertyFilter.notFiltered,
    icon: Building,
    name: "All Homes",
    title: "Homes",
  },
  [EPropertyFilter.withAlerts]: {
    cssColorMethod: EFilterColorMethod.FILL,
    filterTypeEnum: EPropertyFilter.withAlerts,
    icon: Bell,
    name: "Homes with Alerts",
    title: "Bell",
  },
  [EPropertyFilter.withWarnings]: {
    cssColorMethod: EFilterColorMethod.STROKE,
    filterTypeEnum: EPropertyFilter.withWarnings,
    icon: AlertTriangle,
    name: "Homes with Warnings",
    title: "Warning",
  },
};

const RegionDetails: React.FC = () => {
  const [showAddHomeModal, setShowAddHomeModal] = useState(false);
  const [newHomeValue, setNewHomeValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const userId = useReactiveVar(userIdVar);
  const params = useParams();
  const navigate = useNavigate();
  const organizationIdName = useReactiveVar(organizationIdNameVar);
  let { regionId } = params;
  if (!regionId) {
    regionId = "";
  }
  const [currentFilteredType, setFilteredType] = useState<EPropertyFilter>(
    EPropertyFilter.notFiltered
  );
  const [currentFilteredData, setFilteredData] = useState<
    IUnitDetailsDataHome[]
  >([]);
  const propertyTitle = "";
  const propertyAddress = "";
  const { loading, data: regionData } = useQuery<
    IRegionDetailsData,
    IRegionIdVariable
  >(GET_REGION, {
    variables: { regionPropertyId: regionId },
  });
  let searchedData: IUnitDetailsDataHome[];
  useEffect(() => {
    if (!regionId) {
      navigate("/");
    }
    window.scrollTo(0, 0);
  }, [regionId]);
  useEffect(() => {
    setFilteredData(
      _.filter(searchedData, filteredMethods[currentFilteredType])
    );
  }, [regionData, searchValue, currentFilteredType]);
  useEffect(() => {
    smallHeaderTitle(propertyTitle);
    smallHeaderDescription(propertyAddress);
    smallHeaderBackArrow(true);
    return () => {
      smallHeaderTitle("");
      smallHeaderDescription("");
      smallHeaderBackArrow(false);
    };
  }, [regionData]);
  useEffect(() => {
    if (
      organizationIdName.organizationType ===
      EOrganizationType.PRO_VIEW_ORGANIZATION
    ) {
      mixpanel.track("RegionDetails Viewed", {
        organization_id: organizationIdName.organizationId,
        organization_name: organizationIdName.organizationName,
        organization_type: organizationIdName.organizationType,
        region_id: regionId,
        region_name: regionData?.region.propertyName,
        user_id: userId,
      });
    }
  }, []);
  if (loading) {
    return <Spinner />;
  }
  if (!regionData) {
    throw Error("No data was returned");
  }
  searchedData = _.filter(regionData.region.homes, (home) => {
    return home.propertyName.toLowerCase().includes(searchValue.toLowerCase());
  });
  const filteredDataAmounts: IObjectToIUnitDetailsDataHomeAmount = {
    [EPropertyFilter.notFiltered]: searchedData.length,
    [EPropertyFilter.withAlerts]: _.reduce(
      searchedData,
      (result, { alerts }) => {
        return alerts.length !== 0 ? result + 1 : result;
      },
      0
    ),
    [EPropertyFilter.withWarnings]: _.reduce(
      searchedData,
      (result, { warnings }) => {
        return warnings.length !== 0 ? result + 1 : result;
      },
      0
    ),
  };
  const crumbs = [
    {
      name: organizationIdName.organizationName,
      path: `/${organizationIdName.organizationId}/organization`,
    },
    { name: regionData.region.propertyName, path: regionId },
  ];
  const sortedHomes = _.sortBy(currentFilteredData, "propertyName");
  return (
    <div className="dashboard-container">
      {showAddHomeModal && (
        <CreateHomeModal
          closeModal={() => setShowAddHomeModal(false)}
          newHomeValue={newHomeValue}
          setNewHomeValue={setNewHomeValue}
          propertyId={regionId}
        />
      )}
      <div className="dashboard-container-inner">
        <div className="property-details-breadcrumbs">
          <Breadcrumbs
            crumbs={crumbs}
            rootCrumb={{ name: "Organizations", path: "/" }}
          />
        </div>
        <div className="property-details-container">
          <div>
            <div className="property-description-container">
              <div className="property-description-head">
                <Homes
                  title="Homes"
                  className="property-description-head-img"
                />
                <div>
                  <div className="unit-title hidden-name">
                    {regionData.region.propertyName}
                  </div>
                </div>
              </div>
              <form
                className="search-container center-align-as-row"
                onSubmit={(evt: React.SyntheticEvent) => {
                  evt.preventDefault();
                }}
              >
                <Search title="Search" />
                <input
                  type="text"
                  data-testid="search-input"
                  placeholder="Search by home name"
                  className="search-input tool-tip"
                  maxLength={50}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                {searchValue !== "" && (
                  <button
                    type="button"
                    data-testid="search-input-clear"
                    className="tertiary-btn-small"
                    onClick={() => {
                      setSearchValue("");
                    }}
                  >
                    Clear
                  </button>
                )}
              </form>
              <FilterListSelect
                amount={filteredDataAmounts}
                currentFilterEnum={currentFilteredType}
                setFilterData={setFilteredType}
                FilterItemsData={FilterItemsData}
              />
              <FilterList
                amount={filteredDataAmounts}
                currentFilterEnum={currentFilteredType}
                setFilterData={setFilteredType}
                FilterItemsData={FilterItemsData}
              />
            </div>
          </div>
          <div className="property-details-floors-container">
            <button
              type="submit"
              data-testid="add-home-btn"
              className="tertiary-btn-small user-card-action-btn user-card-action-btn-resend margin-bottom-small"
              onClick={() => {
                setShowAddHomeModal(true);
              }}
            >
              + Add Home
            </button>
            <Floor data={sortedHomes} floorName="" moreThanOneFloor={false} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegionDetails;
